import React, {useState, useEffect} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import EditButton from '../common/EditButton';
import DeleteButton from '../common/DeleteButton';
import ListPageLayout from '../common/ListPageLayout';
import SearchButton from '../common/SearchButton';
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {usePermission} from "../../core/PermissionContext";
import {UserPermission} from "../../utils/utils";
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const ProductAutomotiveServiceList = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!loadingPermissions) {
            makeQuery();
        }
    }, [loadingPermissions]);

    async function makeQuery() {
        try {
            setLoading(true);
            const url = `${apiUrl}/api/products/query=${searchTerm}`;
            const response = await makeRequest(url, 'GET');
            setProducts(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleQuery = async (e) => {
        e.preventDefault();
        await makeQuery();
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleEdit = (id) => {
        navigate(`/app/productautomotiveservicemanager?id=${id}`);
    };

    const handleCreate = () => {
        navigate('/app/productautomotiveservicemanager');
    };

    const handleDelete = async (id) => {
        if (!await showConfirm('Deseja realmente excluir este produto?')) {
            return;
        }
        try {
            setLoading(true);
            const url = `${apiUrl}/api/products/${id}`;
            await makeRequest(url, 'DELETE');
            await makeQuery();
        } catch (error) {
            console.error('Error deleting product:', error);
            showAlert('Erro ao excluir produto');
        } finally {
            setLoading(false);
        }
    };

    if (loadingPermissions) {
        return <Loading/>;
    }

    if (!hasUserPermission(UserPermission.LIST_AUTOMOTIVE_SERVICE_PRODUCTS)) {
        return <div>Acesso negado</div>;
    }

    return (
        <ListPageLayout
            tableWidth={1000}
            header={<div className="header-label">Produtos de Serviço Automotivo</div>}
            filters={
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
                    <form onSubmit={handleQuery} style={{display: 'flex', gap: '10px'}}>
                        <input
                            type="text"
                            placeholder="Buscar produtos..."
                            value={searchTerm}
                            onChange={handleInputChange}
                            style={{width: '300px'}}
                        />
                        <SearchButton type="submit"/>
                    </form>

                    {hasUserPermission(UserPermission.CREATE_AUTOMOTIVE_SERVICE_PRODUCTS) && (
                        <button onClick={handleCreate} className="create-button">
                            Cadastrar Produto
                        </button>
                    )}
                </div>
            }
        >
            <table className="data-table">
                <thead>
                    <tr>
                        <th>Código</th>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>Valor</th>
                        <th>Estoque</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product) => (
                        <tr key={product.id}>
                            <td>{product.code}</td>
                            <td>{product.name}</td>
                            <td>{product.type === 'PRODUCT' ? 'Produto' : 'Serviço'}</td>
                            <td>R$ {product.value?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            }) || '0,00'}</td>
                            <td>{product.stock}</td>
                            <td>
                                {hasUserPermission(UserPermission.EDIT_AUTOMOTIVE_SERVICE_PRODUCTS) && (
                                    <EditButton
                                        onClick={() => handleEdit(product.id)}
                                        style={{marginRight: '5px'}}
                                    />
                                )}
                                {hasUserPermission(UserPermission.REMOVE_AUTOMOTIVE_SERVICE_PRODUCTS) && (
                                    <DeleteButton onClick={() => handleDelete(product.id)}/>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {products.length === 0 && (
                <div style={{textAlign: 'center', marginTop: '20px'}}>
                    Nenhum produto encontrado
                </div>
            )}
        </ListPageLayout>
    );
};

export default ProductAutomotiveServiceList;